import { take, catchError, map, switchMap, tap, filter } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { UserService } from '@app/core/user/services/user/user.service';
import { SessionCookieService } from '@app/core/cookies';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import * as localforage from 'localforage';
import { Router } from '@angular/router';
import { Configuration } from '@vestiaire/api-sdk-angular';
import { AuthModal } from '@app/shared/model/interfaces/AuthDialog/AuthModal';
import { LikeService } from '@app/shared/services/like/like.service';
import { PriceDropService } from '@app/shared/services/price-drop/priceDrop.service';
import { WishlistService } from '@app/shared/services/wishlist/wishlist.service';
import { CartService } from '@app/core/cart/services';
import { Observable, of } from 'rxjs';
import { ROUTER_NAVIGATION, ROUTER_CANCEL, RouterCancelAction } from '@ngrx/router-store';

import * as userActions from '../actions/user.actions';
import * as reservationActions from '@app/+product/store/actions/reservation.action';
import { FollowingMemberService } from '@app/shared/services/followingMember/followingMember.service';
import { SetRoute, RouterStateUrl, IAppState } from '@app/store';
import { environment } from '@env/environment';
import { ChatService } from '@app/+chat/services/chat.service';
import { GetPaidService } from '@app/+payment/services/get-paid.service';
import { ResetCounters, SetBadgeMe } from '@app/core/header/store';

@Injectable()
export class UserEffects {
  constructor(
    private store: Store<any>,
    private actions$: Actions,
    private userService: UserService,
    private sessionCookieService: SessionCookieService,
    private configuration: Configuration,
    private router: Router,
    private likeService: LikeService,
    private priceDropService: PriceDropService,
    private wishListService: WishlistService,
    private cartService: CartService,
    private followingMemberService: FollowingMemberService,
    private chatService: ChatService,
    private getPaidService: GetPaidService
  ) {}

  /******** LOGIN  ********/

  /**
   * Determine if the user is authenticated.
   */
  @Effect()
  reconnectUser$: Observable<Action> = this.actions$.pipe(
    ofType(userActions.RECONNECTUSER),
    switchMap((action: userActions.ReconnectUser) => {
      return this.userService.getUserFromSession().pipe(
        map(user =>
          user !== null
            ? new userActions.AuthenticatedSuccess({
                authenticated: true,
                user
              })
            : new userActions.AuthenticatedFailure({
                authenticated: false,
                user: null
              })
        ),
        catchError(error => of(new userActions.AuthenticatedFailure()))
      );
    })
  );

  @Effect()
  authenticatedSuccess$ = this.actions$.pipe(
    ofType(userActions.AUTHENTICATED_SUCCESS),
    map((action: userActions.AuthenticatedSuccess) => action.payload),
    switchMap(action => {
      return [new userActions.GetWelcomeVoucher({ authenticated: action.authenticated })];
    })
  );

  /**
   * Determine if the user is authenticated.
   */
  @Effect()
  authenticated$: Observable<Action> = this.actions$.pipe(
    ofType(userActions.AUTHENTICATED),
    switchMap((action: userActions.Authenticated) => {
      return this.userService.getUserFromSession().pipe(
        map(
          user =>
            new userActions.AuthenticatedSuccess({
              authenticated: user !== null,
              user
            })
        ),
        catchError(error => of(new userActions.AuthenticatedFailure()))
      );
    })
  );

  @Effect()
  login$ = this.actions$.pipe(
    ofType(userActions.LOGIN),
    map((action: userActions.Login) => action.payload),
    switchMap(user => {
      return this.userService.login(user).pipe(
        map(res => {
          this.sessionCookieService.setSessionId(res.id);
          this.sessionCookieService.setUserId(res.user.id);
          this.configuration.apiKeys['__session'] = res.id;
          this.userService.setUserToLocalStorage(res.user);

          return new userActions.AuthenticatedSuccess({
            authenticated: true,
            user: res.user
          });
        }),
        catchError(error => {
          if (user.socialAccountType === 'facebook') {
            return of(new userActions.LoginFailure({ error: error, user: user }));
          } else {
            return of(new userActions.LoginFailure(error));
          }
        })
      );
    })
  );

  @Effect()
  getWalletForUser$ = this.actions$.pipe(
    ofType(userActions.AUTHENTICATED_SUCCESS),
    map((action: userActions.AuthenticatedSuccess) => action.payload),
    filter(action => action.authenticated),
    switchMap(userInfo => {
      return userInfo.user.hasWallet
        ? this.userService.getUserWallet().pipe(
            map(res => {
              return new userActions.GetUserWallet(res);
            })
          )
        : of(new userActions.GetUserWallet(null));
    })
  );

  @Effect({ dispatch: false })
  saveRequestUrlOnRouterCancel$ = this.actions$.pipe(
    ofType(ROUTER_CANCEL),
    map((action: RouterCancelAction<IAppState, RouterStateUrl>) => action.payload),
    tap(({ event }) => {
      this.store.dispatch(new SetRoute(event.url));
    })
  );

  @Effect({ dispatch: false })
  redirectTo$ = this.actions$.pipe(
    ofType(userActions.AUTHENTICATED_SUCCESS),
    tap(action => {
      this.store
        .select('route')
        .pipe(take(1))
        .subscribe(
          res => {
            if (res.requestUrl !== '') {
              this.router.navigateByUrl(res.requestUrl);
            }
          },
          () => {
            // silent catch nothing to do
          }
        );
    })
  );

  @Effect({ dispatch: false })
  getLikedItems$ = this.actions$.pipe(
    ofType(userActions.AUTHENTICATED_SUCCESS),
    tap(action => {
      this.likeService.setLikedItem();
    })
  );

  @Effect({ dispatch: false })
  setOrderuser$ = this.actions$.pipe(
    ofType(userActions.AUTHENTICATED_SUCCESS),
    tap(action => {
      this.cartService.refresh();
    })
  );

  @Effect()
  getWelcomeVoucher$ = this.actions$.pipe(
    ofType(userActions.AUTHENTICATED_SUCCESS),
    map((action: userActions.AuthenticatedSuccess) => action.payload),
    switchMap(action => {
      return this.userService.getWelcomeVoucher(true).pipe(
        map(res => new userActions.GetWelcomeVoucherSuccess(res)),
        catchError(error => of(new userActions.GetWelcomeVoucherFailure()))
      );
    })
  );

  @Effect()
  getWelcomeVoucherOffline$ = this.actions$.pipe(
    ofType(userActions.AUTHENTICATED_FAILURE, userActions.LOGOUT_SUCCESS),
    map((action: userActions.AuthenticatedFailure) => action.payload),
    switchMap(action => {
      return this.userService.getWelcomeVoucher(false).pipe(
        map(res => new userActions.GetWelcomeVoucherSuccess(res)),
        catchError(error => of(new userActions.GetWelcomeVoucherFailure()))
      );
    })
  );

  @Effect()
  logoutSuccess$ = this.actions$.pipe(
    ofType(userActions.LOGOUT_SUCCESS),
    tap(action => this.chatService.logout()),
    switchMap(action => [
      new reservationActions.ClearReservation(),
      new userActions.GetWelcomeVoucher({ authenticated: false }),
      new ResetCounters()
    ])
  );

  @Effect({ dispatch: false })
  getPriceDropFollow$ = this.actions$.pipe(
    ofType(userActions.AUTHENTICATED_SUCCESS),
    tap(action => {
      this.priceDropService.setPriceDropItems();
    })
  );

  @Effect({ dispatch: false })
  getWishedItems$ = this.actions$.pipe(
    ofType(userActions.AUTHENTICATED_SUCCESS),
    tap(action => {
      this.wishListService.setWishesItems();
    })
  );

  @Effect({ dispatch: false })
  getFollowingMember$ = this.actions$.pipe(
    ofType(userActions.AUTHENTICATED_SUCCESS),
    tap(action => {
      this.followingMemberService.setFollowingMember();
    })
  );

  @Effect()
  register$ = this.actions$.pipe(
    ofType(userActions.REGISTER),
    map((action: userActions.Register) => action.payload),
    switchMap(user => {
      return this.userService.registerUser(user).pipe(
        map(res => {
          this.sessionCookieService.setSessionId(res.id);
          this.sessionCookieService.setUserId(res.user.id);
          this.userService.setUserToLocalStorage(res.user);
          this.configuration.apiKeys['__session'] = res.id;

          return new userActions.RegisterSuccess({
            session: res,
            accountType: user.credentials.socialAccountType
          });
        }),
        catchError(error => {
          return of(new userActions.RegisterFailure(error));
        })
      );
    })
  );

  @Effect()
  registerSuccess$ = this.actions$.pipe(
    ofType(userActions.REGISTER_SUCCESS),
    map((action: userActions.RegisterSuccess) => {
      return new userActions.AuthenticatedSuccess({ authenticated: true, user: action.payload.session.user });
    })
  );

  @Effect()
  logout$ = this.actions$.pipe(
    ofType(userActions.LOGOUT),
    switchMap(user => {
      return this.userService.logout().pipe(
        map(res => {
          localforage.removeItem('user');
          localforage.removeItem('likedItem');
          localforage.removeItem('wallet');
          localforage.removeItem('recentlySeenProducts');
          this.sessionCookieService.clear();
          return new userActions.LogoutSuccess();
        }),
        catchError(error => {
          return of(new userActions.LogoutFailure(error));
        })
      );
    })
  );

  @Effect({ dispatch: false })
  fbRegisterFailure$ = this.actions$.pipe(
    ofType(userActions.LOGIN_FAILURE),
    map((action: userActions.RegisterFailure) => action.payload),
    tap(payload => {
      if (payload.error.error && payload.error.error.meta && payload.error.error.meta.linked === false) {
        this.store.dispatch(new userActions.ResetState());
        this.userService.setAuthDialogData({
          authModalType: 'register',
          credentials: payload.user
        } as AuthModal);
      }
    })
  );

  @Effect({ dispatch: false })
  getNeedActionFromGetPaid$ = this.actions$.pipe(
    ofType(userActions.AUTHENTICATED_SUCCESS),
    filter((action: userActions.AuthenticatedSuccess) => action.payload.user.hyperwalletActive),
    tap(_ => {
      this.getPaidService.getNeedAction().subscribe({
        next: res => this.store.dispatch(new SetBadgeMe(res))
      });
    })
  );
}
